import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import FontAwesome from 'react-fontawesome';
import blockEvent from 'react-select/lib/utils/blockEvent';

import { Row } from 'components/layout';
import styles from './option-with-tick.less';

const OptionWithTick = ({
  children,
  className,
  instancePrefix,
  isDisabled,
  isFocused,
  isSelected,
  onFocus,
  onSelect,
  option,
  optionIndex,
}) => {
  const _className = classnames(className, option.className, styles.root);
  const _iconClassName = classnames(styles.icon, {
    [styles['is-selected']]: isSelected,
  });

  // State
  const [dragging, setDragging] = useState(false);

  // Callbacks
  const handleFocus = useCallback(
    event => {
      if (!isFocused) {
        onFocus(option, event);
      }
    },
    [isFocused, option, onFocus]
  );

  const handleMouseDown = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      onSelect(option, event);
    },
    [option, onSelect]
  );

  const handleMouseEnter = handleFocus;
  const handleMouseMove = handleFocus;

  const handleTouchEnd = useCallback(
    event => {
      if (!dragging) {
        handleMouseDown(event);
      }
    },
    [dragging, handleMouseDown]
  );

  const handleTouchMove = useCallback(() => {
    setDragging(true);
  }, [setDragging]);

  const handleTouchStart = useCallback(() => {
    setDragging(false);
  }, [setDragging]);

  if (isDisabled) {
    return (
      <Row
        gutter={false}
        className={_className}
        onMouseDown={blockEvent}
        onClick={blockEvent}
      >
        <FontAwesome name="check" className={_iconClassName} />
        {children}
      </Row>
    );
  }

  return (
    <Row
      gutter={false}
      className={_className}
      style={option.style}
      role="option"
      aria-label={option.label}
      aria-selected={isSelected}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      id={`${instancePrefix}-option-${optionIndex}`}
      title={option.title}
    >
      <FontAwesome name="check" className={_iconClassName} />
      {children}
    </Row>
  );
};

OptionWithTick.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string, // className (based on mouse position)
  instancePrefix: PropTypes.string.isRequired, // unique prefix for the ids (used for aria)
  isDisabled: PropTypes.bool, // the option is disabled
  isFocused: PropTypes.bool, // the option is focused
  isSelected: PropTypes.bool, // the option is selected
  onFocus: PropTypes.func, // method to handle mouseEnter on option element
  onSelect: PropTypes.func, // method to handle click on option element
  onUnfocus: PropTypes.func, // method to handle mouseLeave on option element
  option: PropTypes.object.isRequired, // object that is base for that option
  optionIndex: PropTypes.number, // index of the option, used to generate unique ids for aria
};

export default OptionWithTick;
export { OptionWithTick };
